/* One image viewer for the CRE43 and original product galleries. */
if (!customElements.get('wacc-image-lightbox')) {
  customElements.define('wacc-image-lightbox', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.innerHTML = `<dialog class="wacc-lightbox" aria-label="Enlarged product images">
        <div class="wacc-lightbox__stage"><img class="wacc-lightbox__image" alt="" draggable="false"></div>
        <button class="wacc-lightbox__close" type="button" autofocus aria-label="Close image">Close</button>
        <button class="wacc-lightbox__previous" type="button" aria-label="Previous image">&#8592;</button>
        <button class="wacc-lightbox__next" type="button" aria-label="Next image">&#8594;</button>
        <p class="wacc-lightbox__caption" aria-live="polite"></p>
      </dialog>`;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.wacc-lightbox__stage');
      this.image = this.querySelector('img');
      this.pointers = new Map();
      document.documentElement.classList.add('wacc-lightbox-ready');
      document.addEventListener('click', event => this.openFromGallery(event), options);
      document.addEventListener('keydown', event => {
        if (!['Enter', ' '].includes(event.key) || !event.target.matches('.wacc-gallery__image')) return;
        this.openFromGallery(event);
      }, options);
      this.querySelector('.wacc-lightbox__close').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('.wacc-lightbox__previous').addEventListener('click', () => this.step(-1), options);
      this.querySelector('.wacc-lightbox__next').addEventListener('click', () => this.step(1), options);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow;
        this.pointers.clear();
        this.opener?.focus({ preventScroll: true });
      }, options);
      this.image.addEventListener('load', () => this.paint(), options);
      this.stage.addEventListener('pointerdown', event => this.pointerDown(event), options);
      this.stage.addEventListener('pointermove', event => this.pointerMove(event), options);
      this.stage.addEventListener('pointerup', event => this.pointerUp(event), options);
      this.stage.addEventListener('pointercancel', event => this.pointerUp(event, true), options);
      window.addEventListener('resize', () => this.reset(), options);
      document.addEventListener('cre43:variant-change', () => {
        if (this.dialog.open) this.dialog.close();
      }, options);
    }

    disconnectedCallback() {
      if (this.dialog.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
    }

    openFromGallery(event) {
      const opener = event.target.closest('.cre43-gallery__hero, .wacc-gallery__image');
      if (!opener || opener.closest('[hidden]')) return;
      const cre43 = opener.closest('cre43-gallery');
      if (opener.matches('.cre43-gallery__hero')) {
        // The server-rendered sequence also works before the gallery has booted.
        this.items = cre43.sequence || [...cre43.querySelectorAll('.cre43-gallery__thumb')].map(thumb => ({
          src: thumb.href, alt: thumb.querySelector('img').alt
        }));
        this.index = cre43.index || 0;
        this.activate = index => { if (cre43.sequence) cre43.activate(index); };
      } else {
        const gallery = opener.closest('.wacc-gallery');
        const images = [...gallery.querySelectorAll('.wacc-gallery__slide .wacc-gallery__image')];
        this.items = images.map(image => ({ src: image.closest('[data-zoom-src]').dataset.zoomSrc, alt: image.alt }));
        this.index = images.indexOf(opener);
        this.activate = index => gallery.querySelector(`.wacc-gallery__thumb[data-index="${images[index].closest('[data-index]').dataset.index}"]`)?.click();
      }
      if (!this.items.length) return;
      event.preventDefault();
      this.opener = opener;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.showImage();
      this.dialog.showModal();
    }

    showImage() {
      this.reset();
      const item = this.items[this.index];
      this.image.src = item.src;
      this.image.alt = item.alt;
      this.querySelector('.wacc-lightbox__caption').textContent = `${this.index + 1} of ${this.items.length} - ${item.alt}`;
      this.querySelectorAll('.wacc-lightbox__previous, .wacc-lightbox__next').forEach(button => { button.hidden = this.items.length < 2; });
    }

    step(direction) {
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.activate(this.index);
      this.showImage();
    }

    reset() {
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.pointers?.clear();
      this.paint();
    }

    paint() {
      const limitX = Math.max(0, (this.image.offsetWidth * this.scale - this.stage.clientWidth) / 2);
      const limitY = Math.max(0, (this.image.offsetHeight * this.scale - this.stage.clientHeight) / 2);
      this.x = Math.max(-limitX, Math.min(limitX, this.x));
      this.y = Math.max(-limitY, Math.min(limitY, this.y));
      this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      this.stage.dataset.zoomed = String(this.scale > 1);
    }

    zoom(scale, point, origin = { scale: this.scale, x: this.x, y: this.y }) {
      const rect = this.stage.getBoundingClientRect();
      const ratio = scale / origin.scale;
      this.x = origin.x * ratio + (point.x - rect.left - rect.width / 2) * (1 - ratio);
      this.y = origin.y * ratio + (point.y - rect.top - rect.height / 2) * (1 - ratio);
      this.scale = scale;
      this.paint();
    }

    pointerDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.drag = { x: event.clientX, y: event.clientY, tx: this.x, ty: this.y, moved: false };
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { distance: Math.hypot(b.x - a.x, b.y - a.y), midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, scale: this.scale, x: this.x, y: this.y };
        this.drag.moved = true;
      } else this.pinch = null;
    }

    pointerMove(event) {
      if (!this.pointers.has(event.pointerId)) {
        if (this.scale > 1 && event.pointerType === 'mouse' && !this.pointers.size) {
          const rect = this.stage.getBoundingClientRect();
          this.x = (0.5 - (event.clientX - rect.left) / rect.width) * Math.max(0, this.image.offsetWidth * this.scale - rect.width);
          this.y = (0.5 - (event.clientY - rect.top) / rect.height) * Math.max(0, this.image.offsetHeight * this.scale - rect.height);
          this.paint();
        }
        return;
      }
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const scale = Math.max(1, Math.min(4, this.pinch.scale * Math.hypot(b.x - a.x, b.y - a.y) / Math.max(1, this.pinch.distance)));
        this.zoom(scale, this.pinch.midpoint, this.pinch);
        this.x += (a.x + b.x) / 2 - this.pinch.midpoint.x;
        this.y += (a.y + b.y) / 2 - this.pinch.midpoint.y;
        this.paint();
      } else if (this.drag) {
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (Math.hypot(dx, dy) > 6) this.drag.moved = true;
        if (this.scale > 1) {
          this.x = this.drag.tx + dx;
          this.y = this.drag.ty + dy;
          this.paint();
        }
      }
    }

    pointerUp(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const tap = !cancelled && this.pointers.size === 1 && !this.drag?.moved && !this.pinch;
      this.pointers.delete(event.pointerId);
      if (tap) this.zoom(this.scale > 1 ? 1 : 2, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1) {
        const point = [...this.pointers.values()][0];
        this.drag = { x: point.x, y: point.y, tx: this.x, ty: this.y, moved: true };
      }
      if (!this.pointers.size) { this.drag = null; this.pinch = null; }
    }
  });
}
